import React, { useState, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useChatbotConfig } from '../contexts/ChatbotConfigContext';
import { customBranding, apiEndpoints, validationRegex } from '../config/config';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { 
  FileSpreadsheet, Cloud,
  Plus, 
  Loader2, Lock,
  Bookmark, BookmarkPlus,ChevronDown,
  Eye,
  GripVertical,
  AlertCircle,
  Pencil, FileText, ArrowDownCircle, Settings, MoveVertical, Edit, ToggleLeft
} from 'lucide-react';
import { toast } from 'react-toastify';
import Papa from 'papaparse';
import axios from 'axios';

const API_KEY = 'AIzaSyAqnFjM5HlIQ5RI_9Z5_dVkp23Knq3UhTo';

const inputTypes = [
  'text',
  'number',
  'email',
  'date',
];
// First, create a new DataPreviewAccordion component
const DataPreviewAccordion = ({ currentSheetState, isOpen, setIsOpen }) => {
  return (
    <motion.div 
      className="bg-[#e0eff8] rounded-lg shadow-sm mb-6 overflow-hidden border border-[#a9cfe0]"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-5 hover:bg-[#d0e6f3] transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="bg-[#2888b5] bg-opacity-20 p-2 rounded-full">
            <FileText className="text-[#2888b5]" size={20} />
          </div>
          <h2 className="text-lg font-semibold text-[#1d6285]">Data Preview</h2>
        </div>
        <motion.div
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.3, type: "spring", stiffness: 300 }}
        >
          <ChevronDown size={20} className="text-[#2888b5]" />
        </motion.div>
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div 
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="p-5 border-t border-[#a9cfe0]">
              <div className="overflow-x-auto rounded-lg border border-[#a9cfe0]">
                <table className="min-w-full divide-y divide-[#a9cfe0]">
                  <thead className="bg-[#d0e6f3]">
                    <tr>
                      {currentSheetState?.headers.map((header) => (
                        <th
                          key={header.id}
                          className="px-6 py-3 text-left text-xs font-medium text-[#1d6285] uppercase tracking-wider"
                        >
                          {header.name}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-[#e0eff8]">
                    {currentSheetState?.data.slice(0, 5).map((row, rowIndex) => (
                      <motion.tr 
                        key={rowIndex}
                        initial={{ opacity: 0, y: 5 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.2, delay: rowIndex * 0.05 }}
                        className="hover:bg-[#f0f7fb]"
                      >
                        {currentSheetState.headers.map((header) => (
                          <td
                            key={header.id}
                            className="px-6 py-4 whitespace-nowrap text-sm text-[#1d6285]"
                          >
                            {row[header.name]}
                          </td>
                        ))}
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};


const FormGenerator = () => {
      const { config } = useChatbotConfig();
  const [sheetUrl, setSheetUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [sheets, setSheets] = useState([]);
  const [currentSheetId, setCurrentSheetId] = useState(null);
  const [sheetStates, setSheetStates] = useState({});
  const [newHeaderName, setNewHeaderName] = useState('');
  const [sheetsLoaded, setSheetsLoaded] = useState(false);
  const [isValidating, setIsValidating] = useState(true);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [userToken, setUserToken] = useState(null);
  const [isDataPreviewOpen, setIsDataPreviewOpen] = useState(false);
// Add this state variable to your component
const [activeTab, setActiveTab] = useState('settings');

// Add these state variables to your component (move from FormSettings)
const [formName, setFormName] = useState(
  sheetStates[currentSheetId]?.formSettings?.name || ''
);
const [formDescription, setFormDescription] = useState(
  sheetStates[currentSheetId]?.formSettings?.description || ''
);
const [loginRequired, setLoginRequired] = useState(
  sheetStates[currentSheetId]?.formSettings?.loginRequired || 0
);

// Add this effect to update the form settings when sheet changes
useEffect(() => {
  if (currentSheetId && sheetStates[currentSheetId]) {
    const currentFormSettings = sheetStates[currentSheetId]?.formSettings;
    setFormName(currentFormSettings?.name || '');
    setFormDescription(currentFormSettings?.description || '');
    setLoginRequired(currentFormSettings?.loginRequired || 0);
  }
}, [currentSheetId, sheetStates]);

// Add this handler for updating form settings
const handleBlur = (field, value) => {
  setSheetStates(prev => ({
    ...prev,
    [currentSheetId]: {
      ...prev[currentSheetId],
      formSettings: {
        ...prev[currentSheetId].formSettings,
        [field]: value
      }
    }
  }));
};

// Add this handler for the login toggle
const handleLoginToggle = (e) => {
  const newValue = e.target.checked ? 1 : 0;
  setLoginRequired(newValue);
  handleBlur('loginRequired', newValue);
};
  // Get current sheet's state
  const currentSheetState = currentSheetId ? sheetStates[currentSheetId] : null;

  const extractSheetId = (url) => {
    const regex = /\/spreadsheets\/d\/([a-zA-Z0-9-_]+)/;
    const match = url.match(regex);
    return match ? match[1] : null;
  };

  useEffect(() => {
    const verifyUser = async () => {
      try {
        const params = new URLSearchParams(window.location.search);
        const token  = params.get('userToken');

        if (!token) {
          toast.error('User token not found');
          setIsValidating(false);
          return;
        }
        setUserToken(token);
        localStorage.setItem('userToken', token);


        const response = await axios.get(`${apiEndpoints.form}/verify-user?userId=${token}`);
        
        if (response.data.status) {
          localStorage.setItem('user', JSON.stringify(response.data.user));
          setIsAuthorized(true);
        } else {
          toast.error('Not a valid user');
        }
      } catch (error) {
        console.error('User verification failed:', error);
        toast.error('Not a valid user');
      } finally {
        setIsValidating(false);
      }
    };
    verifyUser();
  }, []);

  
  const validateGoogleSheetUrl = useCallback((url) => {
    return url.includes('docs.google.com/spreadsheets') && extractSheetId(url);
  }, []);

  const fetchSheets = async (spreadsheetId) => {
    try {
      const response = await fetch(
        `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}?key=${API_KEY}`
      );
      
      if (!response.ok) {
        const errorData = await response.json();
        console.error('API Error:', errorData);
        throw new Error(errorData.error?.message || 'Failed to fetch sheets');
      }
      
      const data = await response.json();
      return data.sheets.map(sheet => ({
        id: sheet.properties.sheetId,
        title: sheet.properties.title,
        index: sheet.properties.index
      }));
    } catch (error) {
      console.error('Error fetching sheets:', error);
      throw error;
    }
  };

  const fetchSheetData = async (spreadsheetId, sheetTitle) => {
    try {
      const exportUrl = `https://docs.google.com/spreadsheets/d/${spreadsheetId}/gviz/tq?tqx=out:csv&sheet=${encodeURIComponent(sheetTitle)}`;
      
      const response = await fetch(exportUrl);
      if (!response.ok) {
        throw new Error('Failed to fetch sheet data');
      }
      
      const csvText = await response.text();
      
      // Check if the CSV is completely empty or contains only whitespace/newlines
      if (!csvText.trim()) {
        return { data: [], isEmpty: true };
      }
      
      return new Promise((resolve, reject) => {
        // First parse: Get raw data to check for emptiness
        Papa.parse(csvText, {
          header: false,
          skipEmptyLines: true,
          complete: (rawResults) => {
            console.log('Raw parse results:', rawResults);
            
            // Sheet is only empty if it has no rows at all
            const isEmpty = rawResults.data.length === 0;
            
            // Handle case where we only have one row (headers only)
            if (rawResults.data.length === 1) {
              const headers = rawResults.data[0];
              const headerObjects = headers.map((header, index) => ({
                id: `header-${index}`,
                name: header.trim() || `Column ${index + 1}`,
                type: 'text',
                required: false,
                sampleData: []
              }));
              
              resolve({
                data: [], // No actual data rows
                isEmpty: false, // Not empty since we have headers
                headers: headerObjects,
                meta: { fields: headers }
              });
              return;
            }
            
            // Standard case: Parse with headers for structured data
            Papa.parse(csvText, {
              header: true,
              skipEmptyLines: true,
              transformHeader: (header) => header.trim() || `Column ${rawResults.data[0].indexOf(header) + 1}`,
              complete: (results) => {
                console.log('Standard parse results:', results);
                
                // Create header objects with sample data
                const headerObjects = results.meta.fields.map((header, index) => {
                  return {
                    id: `header-${index}`,
                    name: header,
                    type: 'text',
                    required: false,
                    sampleData: results.data.slice(0, 3).map(row => row[header])
                  };
                });
                
                resolve({
                  data: results.data,
                  isEmpty: isEmpty,
                  headers: headerObjects,
                  meta: results.meta
                });
              },
              error: (error) => {
                console.error('CSV Parse Error:', error);
                reject(error);
              }
            });
          },
          error: (error) => {
            console.error('Raw CSV Parse Error:', error);
            reject(error);
          }
        });
      });
    } catch (error) {
      console.error('Fetch Error:', error);
      throw error;
    }
  };

// Update the sheetStates structure to include an isEmpty flag
const handleLoadData = useCallback(async () => {
  if (!validateGoogleSheetUrl(sheetUrl)) {
    toast.error('Please enter a valid Google Sheets URL');
    return;
  }
 
  setLoading(true);
  try {
    // Reset all states
    setSheets([]);
    setCurrentSheetId(null);
    setSheetStates({});
    setNewHeaderName('');
    
    // Reset form field states
    const spreadsheetId = extractSheetId(sheetUrl);
    const sheetsList = await fetchSheets(spreadsheetId);
    setSheets(sheetsList);
    console.log('Sheets:', sheetsList);
    const newSheetStates = {};
    
    for (const sheet of sheetsList) {
      try {
        const result = await fetchSheetData(spreadsheetId, sheet.title);
        
        newSheetStates[sheet.id] = {
          isEmpty: result.isEmpty,
          sheetTitle: sheet.title,
          // Use the headers directly from the result if available
          headers: result.headers || 
            (result.data[0] ? Object.keys(result.data[0]).map((header, index) => ({
              id: `${sheet.id}-header-${index}`,
              name: header,
              type: 'text',
              required: false,
              isEditing: false,
              sampleData: result.data.slice(0, 3).map(row => row[header])
            })) : []),
          selectedHeaders: [],
          data: result.data || [],
          formSettings: {
            name: '',
            description: ''
          }
        };
      } catch (sheetError) {
        console.error(`Error loading sheet ${sheet.title}:`, sheetError);
        newSheetStates[sheet.id] = {
          isEmpty: true,
          sheetTitle: sheet.title,
          headers: [],
          selectedHeaders: [],
          data: [],
          error: sheetError.message,
          formSettings: {
            name: '',
            description: ''
          }
        };
      }
    }
 
    setSheetStates(newSheetStates);
    setSheetsLoaded(true);
    toast.success('Sheets reloaded successfully!');
  } catch (error) {
    console.error('Error:', error);
    toast.error(error.message || 'Error loading sheets');
  } finally {
    setLoading(false);
  }
}, [sheetUrl, validateGoogleSheetUrl]);

// Add this function to your FormGenerator component
const handleSelectAllHeaders = useCallback(() => {
  if (!currentSheetId || !currentSheetState) {
    toast.error('Please select a sheet first');
    return;
  }

  // Get all header IDs from the current sheet
  const allHeaderIds = currentSheetState.headers.map(header => header.id);
  
  // Check if all headers are already selected
  const allSelected = allHeaderIds.length > 0 && 
    allHeaderIds.every(id => currentSheetState.selectedHeaders.includes(id));
  
  setSheetStates(prev => ({
    ...prev,
    [currentSheetId]: {
      ...prev[currentSheetId],
      // If all are already selected, deselect all. Otherwise, select all.
      selectedHeaders: allSelected ? [] : allHeaderIds
    }
  }));
  
  toast.success(allSelected ? 'All fields deselected' : 'All fields selected');
}, [currentSheetId, currentSheetState]);

// Add EmptySheetMessage component
const EmptySheetMessage = ({ sheetTitle }) => (
  <motion.div 
    className="bg-gradient-to-br from-white to-[#e0eff8] p-8 rounded-lg shadow-sm border border-[#a9cfe0]"
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4, type: "spring" }}
  >
    <div className="flex flex-col items-center justify-center text-center space-y-5">
      <motion.div 
        className="p-4 bg-[#2888b5] bg-opacity-20 rounded-full"
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
      >
        <AlertCircle className="w-10 h-10 text-[#2888b5]" />
      </motion.div>
      
      <h3 className="text-xl font-semibold text-[#1d6285]">Empty Sheet Detected</h3>
      
      <p className="text-[#2888b5] max-w-md">
        The sheet "{sheetTitle}" is currently empty. Please add some data to the sheet before generating a form.
      </p>
      
      <motion.div 
        className="mt-6 p-5 bg-[#f0f7fb] rounded-lg w-full max-w-md border border-[#a9cfe0]"
        whileHover={{ boxShadow: "0 4px 12px rgba(40, 136, 181, 0.15)" }}
      >
        <h4 className="font-medium text-[#1d6285] mb-3">Next Steps:</h4>
        <ol className="text-sm text-[#2888b5] list-decimal list-inside space-y-3">
          <motion.li whileHover={{ x: 5 }} transition={{ type: "spring", stiffness: 300 }}>
            Open your Google Sheet
          </motion.li>
          <motion.li whileHover={{ x: 5 }} transition={{ type: "spring", stiffness: 300 }}>
            Add headers to the first row
          </motion.li>
          <motion.li whileHover={{ x: 5 }} transition={{ type: "spring", stiffness: 300 }}>
            Add some data rows
          </motion.li>
          <motion.li whileHover={{ x: 5 }} transition={{ type: "spring", stiffness: 300 }}>
            Return here and refresh
          </motion.li>
        </ol>
      </motion.div>
    </div>
  </motion.div>
);

// Replace the existing FormSettings component in FormGenerator.js with:

// const FormSettings = React.memo(({ currentSheetId, sheetStates, setSheetStates }) => {
//   const [formName, setFormName] = useState(sheetStates[currentSheetId]?.formSettings?.name || '');
//   const [formDescription, setFormDescription] = useState(sheetStates[currentSheetId]?.formSettings?.description || '');
//   const [loginRequired, setLoginRequired] = useState(sheetStates[currentSheetId]?.formSettings?.loginRequired || 0);
 
//   useEffect(() => {
//     const currentFormSettings = sheetStates[currentSheetId]?.formSettings;
//     setFormName(currentFormSettings?.name || '');
//     setFormDescription(currentFormSettings?.description || '');
//     setLoginRequired(currentFormSettings?.loginRequired || 0);
//   }, [currentSheetId, sheetStates]);
 
//   const handleBlur = (field, value) => {
//     setSheetStates(prev => ({
//       ...prev,
//       [currentSheetId]: {
//         ...prev[currentSheetId],
//         formSettings: {
//           ...prev[currentSheetId].formSettings,
//           [field]: value
//         }
//       }
//     }));
//   };

//   const handleLoginToggle = (e) => {
//     const newValue = e.target.checked ? 1 : 0;
//     setLoginRequired(newValue);
//     handleBlur('loginRequired', newValue);
//   };
 
//   return (
//     <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
//       <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
//         <Settings size={20} className="text-blue-500" />
//         Form Settings
//       </h3>
//       <div className="space-y-4">
//         <div>
//           <label className="block text-sm font-medium text-gray-700 mb-1">
//             Form Name
//           </label>
//           <input
//             type="text"
//             value={formName}
//             onChange={(e) => setFormName(e.target.value)}
//             onBlur={() => handleBlur('name', formName)}
//             className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500"
//             placeholder="Enter form name..."
//           />
//         </div>
//         <div>
//           <label className="block text-sm font-medium text-gray-700 mb-1">
//             Description
//           </label>
//           <textarea
//             value={formDescription}
//             onChange={(e) => setFormDescription(e.target.value)}
//             onBlur={() => handleBlur('description', formDescription)}
//             className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500"
//             rows="3"
//             placeholder="Enter form description..."
//           />
//         </div>
//         <div className="flex items-center justify-between py-2 border-t">
//           <div className="flex items-center gap-2">
//             <Lock className="w-5 h-5 text-gray-500" />
//             <div>
//               <h4 className="text-sm font-medium text-gray-900">Require Login</h4>
//               <p className="text-sm text-gray-500">Users must log in to access this form</p>
//             </div>
//           </div>
//           <label className="relative inline-flex items-center cursor-pointer">
//             <input
//               type="checkbox"
//               checked={loginRequired === 1}
//               onChange={handleLoginToggle}
//               className="sr-only peer"
//             />
//             <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
//           </label>
//         </div>
//       </div>
//     </div>
//   );
// });

  const handleSheetChange = useCallback((sheetId) => {
    setCurrentSheetId(sheetId);
  }, []);

  const handleAddCustomHeader = useCallback(() => {
    if (!currentSheetId) {
      toast.error('Please load a sheet first');
      return;
    }

    if (!newHeaderName.trim()) {
      toast.error('Please enter a header name');
      return;
    }

    setSheetStates(prev => {
      const currentSheet = prev[currentSheetId];
      const newHeader = {
        id: `${currentSheetId}-header-${currentSheet.headers.length}`,
        name: newHeaderName,
        type: 'text',
        required: false
      };

      return {
        ...prev,
        [currentSheetId]: {
          ...currentSheet,
          headers: [...currentSheet.headers, newHeader]
        }
      };
    });

    setNewHeaderName('');
    toast.success('Custom header added!');
  }, [currentSheetId, newHeaderName]);

  const handleHeaderTypeChange = useCallback((headerId, newType) => {
    if (!currentSheetId) return;

    setSheetStates(prev => {
      const currentSheet = prev[currentSheetId];
      const updatedHeaders = currentSheet.headers.map(header =>
        header.id === headerId ? { ...header, type: newType } : header
      );

      return {
        ...prev,
        [currentSheetId]: {
          ...currentSheet,
          headers: updatedHeaders
        }
      };
    });
  }, [currentSheetId]);

  const handleHeaderRequiredChange = useCallback((headerId) => {
    if (!currentSheetId) return;

    setSheetStates(prev => {
      const currentSheet = prev[currentSheetId];
      const updatedHeaders = currentSheet.headers.map(header =>
        header.id === headerId ? { ...header, required: !header.required } : header
      );

      return {
        ...prev,
        [currentSheetId]: {
          ...currentSheet,
          headers: updatedHeaders
        }
      };
    });
  }, [currentSheetId]);

  const handleDragEnd = useCallback((result) => {
    if (!result.destination || !currentSheetId) return;
    
    const { source, destination } = result;
    
    if (source.droppableId === destination.droppableId && 
        source.index === destination.index) {
      return;
    }

    setSheetStates(prev => {
      const currentSheet = prev[currentSheetId];
      const newHeaders = Array.from(currentSheet.headers);
      const [removed] = newHeaders.splice(source.index, 1);
      newHeaders.splice(destination.index, 0, removed);

      return {
        ...prev,
        [currentSheetId]: {
          ...currentSheet,
          headers: newHeaders
        }
      };
    });
  }, [currentSheetId]);


  const toCamelCase = (str) => {
    return str.toLowerCase().replace(/[^a-zA-Z0-9]+(.)/g, (m, chr) => chr.toUpperCase());
  };

  // Add new function to create placeholder text
  const createPlaceholder = (label) => {
    return `Enter your ${label.toLowerCase()}`;
  };

  // Add function to prepare form data for API
  const prepareFormData = useCallback(() => {
    if (!currentSheetState || !currentSheetId || !userToken) return null;

    const selectedFields = currentSheetState.headers
      .filter(header => currentSheetState.selectedHeaders.includes(header.id))
      .map(header => ({
        label: header.name,
        type: header.type,
        required: header.required,
        // name: header.name,
        placeHolder: createPlaceholder(header.name)
      }));
      const formSettings = currentSheetState.formSettings || {};

    return {
      userId: userToken,
      googleSheetsUrl: sheetUrl,
      sheetName: currentSheetState.sheetTitle,
      name: formSettings.name,  
      description: formSettings.description,
      settings: {
        loginRequired: formSettings.loginRequired || 0
      },
      jdata: selectedFields,
      dueDate: "2025-01-01"
    };
  }, [currentSheetState, currentSheetId, sheetUrl, userToken]);


// Modify handleCreateForm to include API call
  const handleCreateForm = useCallback(async () => {
    if (!currentSheetState) {
      toast.error('Please select a sheet first');
      return;
    }

    if (currentSheetState.selectedHeaders.length === 0) {
      toast.error('Please select fields for the form');
      return;
    }

    const formData = prepareFormData();
    if (!formData) {
      toast.error('Error preparing form data');
      return;
    }

    try {
      const response = await axios.post(
     apiEndpoints.form,
        formData,
        {
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            // 'clientToken': `Bearer ${config.accessToken}`
          }
        }
      );

      if (response.status === 201) {
        toast.success('Form created successfully!');
        // Open the link in the existing tab
        window.location.href = 'https://bolofy.com/client/forms/view';
      }
} catch (error) {
  console.error('Error creating form:', error);
  toast.error(error.response?.data?.message || 'Error creating form');
}
}, [currentSheetState, prepareFormData]);

  const handleToggleHeaderSelection = useCallback((headerId) => {
    if (!currentSheetId) return;

    setSheetStates(prev => {
      const currentSheet = prev[currentSheetId];
      const isSelected = currentSheet.selectedHeaders.includes(headerId);
      
      return {
        ...prev,
        [currentSheetId]: {
          ...currentSheet,
          selectedHeaders: isSelected
            ? currentSheet.selectedHeaders.filter(id => id !== headerId)
            : [...currentSheet.selectedHeaders, headerId]
        }
      };
    });
  }, [currentSheetId]);

  // if (isValidating) {
  //   return (
  //     <div className="min-h-screen flex items-center justify-center">
  //       <div className="flex flex-col items-center space-y-4">
  //         <motion.div
  //           animate={{ rotate: 360 }}
  //           transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
  //         >
  //           <div className="w-12 h-12 border-4 border-t-blue-500 border-b-blue-500 rounded-full" />
  //         </motion.div>
  //         <p className="text-gray-600">Verifying user...</p>
  //       </div>
  //     </div>
  //   );
  // }
  // if (!isAuthorized) {
  //   return (
  //     <div className="min-h-screen flex items-center justify-center p-4">
  //       <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full text-center">
  //         <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
  //         <h2 className="text-2xl font-bold text-gray-800 mb-2">Access Denied</h2>
  //         <p className="text-gray-600">You are not authorized to access this page.</p>
  //       </div>
  //     </div>
  //   );
  // }

  return (
    <div className="min-h-screen bg-[#e5f1f7] p-8">
   

    <div className="max-w-7xl mx-auto">
    <motion.div 
  className="flex items-center justify-between mb-8"
  initial={{ opacity: 0, y: -20 }}
  animate={{ opacity: 1, y: 0 }}
  transition={{ duration: 0.4 }}
>
  <div className="flex items-center space-x-4">
    <motion.div
      whileHover={{ rotate: [0, -10, 10, -10, 0] }}
      transition={{ duration: 0.5 }}
    >
      <img
        src={config.logo_url}
        alt={`${customBranding.name} Logo`}
        className="h-12 w-auto object-contain rounded-lg shadow-sm"
      />
    </motion.div>
    <div>
      <motion.h1 
        className="text-2xl font-bold bg-gradient-to-r from-[#2888b5] to-[#175d7f] bg-clip-text text-transparent"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
      >
        Bolofy Form Generator
      </motion.h1>
      <motion.p 
        className="text-sm text-[#2888b5]"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
      >
        Convert Google Sheets to Dynamic Forms in seconds
      </motion.p>
    </div>
  </div>
  
  <motion.div 
    className="flex items-center space-x-4"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{ delay: 0.4 }}
  >
    <div className="text-right font-medium bg-gradient-to-r from-[#2888b5] to-[#1d6285] px-4 py-2 rounded-lg text-white shadow-sm">
      Powered by <span className="font-bold">{`${config.name} `}</span>
    </div>
  </motion.div>
</motion.div> 
      {/* <h1 className="text-3xl font-bold text-gray-800 mb-8">Dynamic Form Generator</h1> */}

      {/* Sequence Section */}
      <div className="bg-white p-6 rounded-lg shadow-sm mb-8">
        
        <div className="space-y-4">
          {/* URL Input */}
          {/* URL Input */}
<motion.div 
  className="flex gap-4 items-center"
  initial={{ opacity: 0, y: 10 }}
  animate={{ opacity: 1, y: 0 }}
  transition={{ duration: 0.3 }}
>
  <div className="flex-1 relative">
    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
      <FileSpreadsheet size={20} className="text-[#2888b5]" />
    </div>
    <motion.input
      whileFocus={{ scale: 1.01, boxShadow: "0 0 0 2px rgba(40, 136, 181, 0.3)" }}
      type="text"
      value={sheetUrl}
      onChange={(e) => setSheetUrl(e.target.value)}
      placeholder="Enter Google Sheet URL"
      className="w-full pl-10 pr-3 py-3 border border-[#a9cfe0] rounded-lg focus:ring-2 outline-none focus:ring-[#2888b5] bg-white transition-all duration-200"
    />
  </div>
  <motion.button
    whileHover={{ scale: 1.03, backgroundColor: "#1d6285" }}
    whileTap={{ scale: 0.97 }}
    initial={{ boxShadow: "0 0 0 rgba(40, 136, 181, 0)" }}
    animate={{ boxShadow: loading ? "0 0 20px rgba(40, 136, 181, 0.5)" : "0 0 0 rgba(40, 136, 181, 0)" }}
    transition={{ type: "spring", stiffness: 400, damping: 10 }}
    onClick={handleLoadData}
    disabled={loading}
    className="bg-[#2888b5] text-white px-6 py-3 rounded-lg flex items-center gap-2 disabled:opacity-50 font-medium shadow-sm"
  >
    {loading ? (
      <Loader2 className="animate-spin" size={20} />
    ) : (
      <FileSpreadsheet size={20} />
    )}
    {loading ? "Loading..." : "Load Data"}
  </motion.button>
</motion.div>


          {/* Sheet Selector */}
          {sheets.length > 0 && (
  <motion.div 
    className="flex items-center gap-4 mt-4"
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.3, delay: 0.1 }}
  >
    <div className="relative flex-1">
      <select
        value={currentSheetId || ''}
        onChange={(e) => handleSheetChange(e.target.value)}
        className="appearance-none block w-full p-3 pl-4 pr-10 border border-[#a9cfe0] rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#2888b5] transition-all duration-200"
      >
        <option value="">Select a sheet</option>
        {sheets.map(sheet => (
          <option key={sheet.id} value={sheet.id}>
            {sheet.title} {sheetStates[sheet.id]?.isEmpty ? ' (Empty)' : ''}
          </option>
        ))}
      </select>
      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-[#2888b5]">
        <ChevronDown size={16} />
      </div>
    </div>
    {currentSheetState?.isEmpty && (
      <motion.span 
        className="text-amber-600 text-sm flex items-center gap-1 bg-amber-50 px-3 py-1 rounded-full border border-amber-200"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.2 }}
      >
        <AlertCircle className="w-4 h-4" />
        Current sheet is empty
      </motion.span>
    )}
  </motion.div>
)}

 </div>
      </div>

       {/* Data Preview Accordion */}
       {currentSheetState && !currentSheetState.isEmpty && (
          <DataPreviewAccordion 
            currentSheetState={currentSheetState}
            isOpen={isDataPreviewOpen}
            setIsOpen={setIsDataPreviewOpen}
          />
        )}

      {!sheetsLoaded && (
  
  <motion.div 
  initial={{ opacity: 0, y: 20 }}
  animate={{ opacity: 1, y: 0 }}
  transition={{ duration: 0.6 }}
  className="mt-8 mb-12 text-center"
>
  <motion.div 
    className="flex justify-center mb-4"
    animate={{ 
      scale: [1, 1.1, 1],
      rotate: [0, 2, -2, 0]  
    }}
    transition={{ 
      duration: 2, 
      repeat: Infinity, 
      repeatDelay: 2
    }}
  >
    <div className="bg-[#2888b5] bg-opacity-20 p-4 rounded-full">
      <FileText size={48} className="text-[#2888b5]" />
    </div>
  </motion.div>

  <motion.h2 
    className="text-3xl font-bold text-[#1d6285] mb-4"
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: 0.2, duration: 0.5 }}
  >
    Welcome to Dynamic Form Generator
  </motion.h2>

  <motion.div 
    className="max-w-2xl mx-auto bg-gradient-to-b from-white to-[#e0eff8] p-8 rounded-xl shadow-sm border border-[#a9cfe0]"
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: 0.3, duration: 0.5 }}
    whileHover={{ boxShadow: "0 10px 25px -5px rgba(40, 136, 181, 0.2)" }}
  >
    <div className="flex items-start space-x-4 mb-6">
      <div className="bg-[#2888b5] bg-opacity-20 p-2 rounded-full flex-shrink-0 mt-1">
        <ArrowDownCircle className="text-[#2888b5]" size={20} />
      </div>
      <p className="text-[#1d6285] text-left">
        Paste your Google Sheet URL above to instantly convert it into a customizable form. Our tool makes it easy to create professional forms in minutes.
      </p>
    </div>

    <div className="grid grid-cols-2 gap-6 text-left text-[#1d6285]">
      <motion.div 
        className="flex items-center space-x-3 p-3 rounded-lg hover:bg-[#d0e6f3]"
        whileHover={{ x: 5 }}
        transition={{ type: "spring", stiffness: 300 }}
      >
        <div className="bg-[#2888b5] bg-opacity-20 p-2 rounded-full">
          <Settings size={20} className="text-[#2888b5]" />
        </div>
        <span>Customize field types</span>
      </motion.div>
      
      <motion.div 
        className="flex items-center space-x-3 p-3 rounded-lg hover:bg-[#d0e6f3]"
        whileHover={{ x: 5 }}
        transition={{ type: "spring", stiffness: 300 }}
      >
        <div className="bg-[#2888b5] bg-opacity-20 p-2 rounded-full">
          <MoveVertical size={20} className="text-[#2888b5]" />
        </div>
        <span>Reorder fields with drag & drop</span>
      </motion.div>
      
      <motion.div 
        className="flex items-center space-x-3 p-3 rounded-lg hover:bg-[#d0e6f3]"
        whileHover={{ x: 5 }}
        transition={{ type: "spring", stiffness: 300 }}
      >
        <div className="bg-[#2888b5] bg-opacity-20 p-2 rounded-full">
          <Edit size={20} className="text-[#2888b5]" />
        </div>
        <span>Edit field names and options</span>
      </motion.div>
      
      <motion.div 
        className="flex items-center space-x-3 p-3 rounded-lg hover:bg-[#d0e6f3]"
        whileHover={{ x: 5 }}
        transition={{ type: "spring", stiffness: 300 }}
      >
        <div className="bg-[#2888b5] bg-opacity-20 p-2 rounded-full">
          <ToggleLeft size={20} className="text-[#2888b5]" />
        </div>
        <span>Toggle required fields</span>
      </motion.div>
    </div>
  </motion.div>
</motion.div>
  )}

      {currentSheetState && !currentSheetState.isEmpty && (
        <div className="grid grid-cols-2 gap-8">
          {/* Form Fields Section */}
          {/* Form Fields Header Section with Better UI */}
          <div className="bg-white bg-gradient-to-br from-white to-[#e0eff8] p-6 rounded-lg shadow-sm border border-[#a9cfe0] mb-6">
  <div className="flex justify-between items-center mb-4">
    <h2 className="text-xl font-semibold text-[#1d6285]">Form Fields</h2>
    {currentSheetState && currentSheetState.headers.length > 0 && (
      <div className="flex items-center gap-4">
        <div className="text-sm text-[#2888b5]">
          {currentSheetState.selectedHeaders.length} of {currentSheetState.headers.length} fields selected
        </div>
        <motion.button
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.97 }}
          onClick={handleSelectAllHeaders}
          className={`px-4 py-2 rounded-md flex items-center gap-2 font-medium text-sm transition-colors shadow-sm ${
            currentSheetState.headers.length > 0 && 
            currentSheetState.headers.every(header => 
              currentSheetState.selectedHeaders.includes(header.id))
              ? 'bg-[#d0e6f3] text-[#1d6285] hover:bg-[#a9cfe0] border border-[#a9cfe0]'
              : 'bg-gradient-to-r from-[#2888b5] to-[#1d6285] text-white hover:from-[#1d6285] hover:to-[#2888b5]'
          }`}
        >
          {currentSheetState.headers.length > 0 && 
           currentSheetState.headers.every(header => 
             currentSheetState.selectedHeaders.includes(header.id)) 
            ? (
              <>
                <Eye size={18} />
                Deselect All Fields
              </>
            ) : (
              <>
                <motion.div 
                  animate={{ scale: [1, 1.2, 1] }} 
                  transition={{ duration: 1, repeat: Infinity, repeatDelay: 2 }}
                >
                  <Bookmark size={18} />
                </motion.div>
                Select All Fields
              </>
            )}
        </motion.button>
      </div>
    )}
  </div>

  {/* Improved Add Custom Field UI */}
  <div className="mb-6 p-4 bg-[#e0eff8] rounded-lg border border-[#a9cfe0]">
    <div className="flex gap-2">
      <input
        type="text"
        value={newHeaderName}
        onChange={(e) => setNewHeaderName(e.target.value)}
        placeholder="Add custom field"
        className="flex-1 p-2 border border-[#a9cfe0] rounded-lg focus:ring-2 focus:ring-[#2888b5] outline-none"
      />
      <motion.button
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        onClick={handleAddCustomHeader}
        disabled={!newHeaderName.trim()}
        className={`p-2 rounded-lg flex items-center justify-center ${
          newHeaderName.trim() 
            ? 'bg-[#2888b5] text-white hover:bg-[#1d6285]' 
            : 'bg-gray-200 text-gray-400 cursor-not-allowed'
        }`}
      >
        <Plus size={20} />
      </motion.button>
    </div>
  </div>


            {/* Headers List */}
            <DragDropContext onDragEnd={handleDragEnd}>
    <Droppable droppableId="headers-list">
      {(provided) => (
        <div {...provided.droppableProps} ref={provided.innerRef} className="space-y-3">
          <AnimatePresence>
            {currentSheetState.headers.map((header, index) => (
              <Draggable key={header.id} draggableId={header.id} index={index}>
                {(provided) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    className={`p-4 border rounded-lg  ${
                      currentSheetState.selectedHeaders.includes(header.id)
                        ? 'border-blue-300 bg-blue-50 shadow-sm'
                      : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      <div {...provided.dragHandleProps}>
                        <GripVertical className="text-gray-400" />
                      </div>
                      <div className="flex-1">
                        {header.isEditing ? (
                          <input
                            type="text"
                            value={header.editName ?? header.name}
                            onChange={(e) => {
                              setSheetStates(prev => ({
                                ...prev,
                                [currentSheetId]: {
                                  ...prev[currentSheetId],
                                  headers: prev[currentSheetId].headers.map(h => 
                                    h.id === header.id ? { ...h, editName: e.target.value } : h
                                  )
                                }
                              }));
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === 'Escape') {
                                const newName = header.editName?.trim() || header.name;
                                setSheetStates(prev => ({
                                  ...prev,
                                  [currentSheetId]: {
                                    ...prev[currentSheetId],
                                    headers: prev[currentSheetId].headers.map(h => 
                                      h.id === header.id ? {
                                        ...h,
                                        name: newName,
                                        isEditing: false,
                                        editName: undefined
                                      } : h
                                    )
                                  }
                                }));
                              }
                            }}
                            onBlur={() => {
                              const newName = header.editName?.trim() || header.name;
                              setSheetStates(prev => ({
                                ...prev,
                                [currentSheetId]: {
                                  ...prev[currentSheetId],
                                  headers: prev[currentSheetId].headers.map(h => 
                                    h.id === header.id ? {
                                      ...h,
                                      name: newName,
                                      isEditing: false,
                                      editName: undefined
                                    } : h
                                  )
                                }
                              }));
                            }}
                            className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 focus:outline-none"
                            autoFocus
                            initial={{ scale: 0.95 }}
                            animate={{ scale: 1 }}
                          />
                        ) : (
                          <div className="flex items-center gap-2">
                            <div className="font-medium">{header.name}</div>
                            <button
                              onClick={() => {
                                setSheetStates(prev => ({
                                  ...prev,
                                  [currentSheetId]: {
                                    ...prev[currentSheetId],
                                    headers: prev[currentSheetId].headers.map(h => 
                                      h.id === header.id ? { ...h, isEditing: true, editName: h.name } : h
                                    )
                                  }
                                }));
                              }}
                              className="p-1 hover:bg-gray-100 rounded"
                            >
                              <Pencil size={16} className="text-gray-500" />
                            </button>
                          </div>
                        )}
                      </div>
                      <select
                        value={header.type}
                        onChange={(e) => handleHeaderTypeChange(header.id, e.target.value)}
                        className="p-2 border rounded"
                      >
                        {inputTypes.map(type => (
                          <option key={type} value={type}>
                            {type.charAt(0).toUpperCase() + type.slice(1)}
                          </option>
                        ))}
                      </select>
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={header.required}
                          onChange={() => handleHeaderRequiredChange(header.id)}
                          className="w-4 h-4"
                        />
                        <span className="text-sm text-gray-600">Required</span>
                      </div>
                      <div className="flex items-center">
                            <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={currentSheetState.selectedHeaders.includes(header.id)}
                            onChange={() => handleToggleHeaderSelection(header.id)}
                            className="sr-only peer"
                          />
                          <div className="w-7 h-4 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:bg-blue-500"></div>
                        </label>
                      </div>  
                  </div>
                </div>
              )}
            </Draggable>
          ))}
          {provided.placeholder}
        </AnimatePresence>
      </div>
    )}
  </Droppable>
  </DragDropContext>
          </div>

              <div>
                

          {/* Form Preview Section */}
         {/* Form Preview Section with Integrated Settings */}
         <div className="bg-white bg-gradient-to-br from-white to-[#e0eff8] p-6 rounded-lg shadow-sm border border-[#a9cfe0]">
  <div className="flex justify-between items-center mb-6">
    <h2 className="text-xl font-semibold text-[#1d6285]">Form Configuration</h2>
    <motion.button
      whileHover={{ scale: 1.02, boxShadow: "0 4px 6px -1px rgba(40, 136, 181, 0.3)" }}
      whileTap={{ scale: 0.98 }}
      onClick={handleCreateForm}
      className="bg-gradient-to-r from-[#2888b5] to-[#1d6285] text-white px-4 py-2 rounded-lg shadow-sm"
      disabled={currentSheetState.headers.length === 0}
    >
      Create Form
    </motion.button>
  </div>

  {/* Tabs for Settings and Preview */}
  <div className="mb-6">
    <div className="flex border-b border-[#a9cfe0]">
      <button
        onClick={() => setActiveTab('settings')}
        className={`px-4 py-2 font-medium text-sm ${
          activeTab === 'settings'
            ? 'text-[#2888b5] border-b-2 border-[#2888b5]'
            : 'text-[#1d6285] hover:text-[#2888b5]'
        }`}
      >
        Form Settings
      </button>
      <button
        onClick={() => setActiveTab('preview')}
        className={`px-4 py-2 font-medium text-sm ${
          activeTab === 'preview'
            ? 'text-[#2888b5] border-b-2 border-[#2888b5]'
            : 'text-[#1d6285] hover:text-[#2888b5]'
        }`}
      >
        Preview
      </button>
    </div>
  </div>

  {/* Settings and Preview Content with Animation */}
  <AnimatePresence mode="wait">
    {activeTab === 'settings' ? (
      <motion.div
        key="settings"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        transition={{ duration: 0.2 }}
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-[#1d6285] mb-1">
              Form Name
            </label>
            <input
              type="text"
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              onBlur={() => handleBlur('name', formName)}
              className="w-full p-2 border border-[#a9cfe0] rounded-md focus:ring-2 focus:ring-[#2888b5] bg-white"
              placeholder="Enter form name..."
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#1d6285] mb-1">
              Description
            </label>
            <textarea
              value={formDescription}
              onChange={(e) => setFormDescription(e.target.value)}
              onBlur={() => handleBlur('description', formDescription)}
              className="w-full p-2 border border-[#a9cfe0] rounded-md focus:ring-2 focus:ring-[#2888b5] bg-white"
              rows="3"
              placeholder="Enter form description..."
            />
          </div>
          <div className="flex items-center justify-between py-2 border-t border-[#a9cfe0]">
            <div className="flex items-center gap-2">
              <Lock className="w-5 h-5 text-[#2888b5]" />
              <div>
                <h4 className="text-sm font-medium text-[#1d6285]">Require Login</h4>
                <p className="text-sm text-[#2888b5]">Users must log in to access this form</p>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={loginRequired === 1}
                onChange={handleLoginToggle}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#a9cfe0] rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#2888b5]"></div>
            </label>
          </div>
        </div>
      </motion.div>
    ) : (
      <motion.div
        key="preview"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        transition={{ duration: 0.2 }}
        className="border-2 border-dashed border-[#a9cfe0] rounded-lg p-6 bg-[#f0f7fb]"
      >
        {currentSheetState.selectedHeaders.length > 0 ? (
          <div className="space-y-4">
            {currentSheetState.headers
              .filter(header => currentSheetState.selectedHeaders.includes(header.id))
              .map(header => (
                <motion.div 
                  key={header.id} 
                  className="mb-4"
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <label className="block text-[#1d6285] text-sm font-bold mb-2">
                    {header.name}
                    {header.required && <span className="text-red-500 ml-1">*</span>}
                  </label>
                  {header.type === 'textarea' ? (
                    <textarea
                      className="w-full p-2 border border-[#a9cfe0] rounded bg-white focus:ring-2 focus:ring-[#2888b5] focus:outline-none transition-all duration-200"
                      placeholder={`Enter ${header.name.toLowerCase()}`}
                      required={header.required}
                    />
                  ) : header.type === 'checkbox' ? (
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        className="w-4 h-4 mr-2 text-[#2888b5]"
                        required={header.required}
                      />
                      <span className="text-sm text-[#1d6285]">{header.name}</span>
                    </div>
                  ) : (
                    <input
                      type={header.type}
                      className="w-full p-2 border border-[#a9cfe0] rounded bg-white focus:ring-2 focus:ring-[#2888b5] focus:outline-none transition-all duration-200"
                      placeholder={`Enter ${header.name.toLowerCase()}`}
                      required={header.required}
                    />
                  )}
                </motion.div>
              ))}
            
            <motion.div 
              className="mt-8 pt-4 border-t border-[#a9cfe0] flex justify-end"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              <button className="px-6 py-2.5 bg-gradient-to-r from-[#2888b5] to-[#1d6285] text-white font-medium rounded-lg shadow-sm hover:from-[#1d6285] hover:to-[#2888b5] transition-colors">
                Submit Form
              </button>
            </motion.div>
          </div>
        ) : (
          <div className="text-center py-12 text-[#2888b5]">
            <motion.div
              initial={{ scale: 0.9, opacity: 0.5 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ 
                duration: 3, 
                repeat: Infinity, 
                repeatType: "reverse" 
              }}
            >
              <Eye className="mx-auto mb-2" size={24} />
              <p>Select fields to preview</p>
            </motion.div>
          </div>
        )}
      </motion.div>
    )}
  </AnimatePresence>
</div>


</div>

          {/* Data Preview Section */}
          {/* <div className="bg-white p-6 rounded-lg shadow-sm col-span-2">
            <h2 className="text-xl font-semibold mb-6">Data Preview</h2>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {currentSheetState.headers.map((header) => (
                      <th
                        key={header.id}
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        {header.name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {currentSheetState.data.slice(0, 5).map((row, rowIndex) => (
                    <tr key={rowIndex}>
                      {currentSheetState.headers.map((header) => (
                        <td
                          key={header.id}
                          className="px-6 py-4 whitespace-nowrap text-sm text-gray-500"
                        >
                          {row[header.name]}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div> */}
        </div>
        
      )}

      {currentSheetState?.isEmpty && (
        <EmptySheetMessage 
          sheetTitle={currentSheetState.sheetTitle}
          error={currentSheetState.error}
        />
      )}
    </div>

{isValidating && (
  <motion.div 
    className="min-h-screen flex items-center justify-center bg-[#e5f1f7]"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
  >
    <div className="flex flex-col items-center space-y-4">
      <motion.div
        animate={{ 
          rotate: 360,
          boxShadow: ["0 0 10px rgba(40, 136, 181, 0.5)", "0 0 20px rgba(40, 136, 181, 0.5)", "0 0 10px rgba(40, 136, 181, 0.5)"]
        }}
        transition={{ 
          rotate: { duration: 1.5, repeat: Infinity, ease: "linear" },
          boxShadow: { duration: 1.5, repeat: Infinity, ease: "easeInOut" }
        }}
      >
        <div className="w-16 h-16 border-4 border-t-[#2888b5] border-b-[#2888b5] border-r-transparent border-l-transparent rounded-full" />
      </motion.div>
      <motion.p
        className="text-[#1d6285] font-medium"
        animate={{ opacity: [0.5, 1, 0.5] }}
        transition={{ duration: 1.5, repeat: Infinity }}
      >
        Verifying your credentials...
      </motion.p>
    </div>
  </motion.div>
)}

{!isValidating && !isAuthorized && (
  <motion.div 
    className="min-h-screen flex items-center justify-center p-4 bg-[#e5f1f7]"
    initial={{ opacity: 0, scale: 0.9 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ duration: 0.5, type: "spring" }}
  >
    <motion.div 
      className="bg-gradient-to-br from-white to-[#e0eff8] p-8 rounded-xl shadow-lg max-w-md w-full text-center border border-[#a9cfe0]"
      whileHover={{ boxShadow: "0 20px 25px -5px rgba(40, 136, 181, 0.2), 0 10px 10px -5px rgba(40, 136, 181, 0.1)" }}
    >
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1, rotate: [0, 10, -10, 0] }}
        transition={{ type: "spring", stiffness: 200, delay: 0.2 }}
        className="bg-[#2888b5] bg-opacity-20 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6"
      >
        <AlertCircle className="w-10 h-10 text-[#2888b5]" />
      </motion.div>
      <motion.h2 
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="text-2xl font-bold text-[#1d6285] mb-3"
      >
        Access Denied
      </motion.h2>
      <motion.p 
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="text-[#2888b5] mb-6"
      >
        You are not authorized to access this page. Please contact the system administrator if you believe this is an error.
      </motion.p>
      <motion.button
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        className="px-6 py-3 bg-gradient-to-r from-[#2888b5] to-[#1d6285] text-white font-medium rounded-lg shadow hover:from-[#1d6285] hover:to-[#2888b5] transition-colors"
        onClick={() => window.location.href = 'https://bolofy.com'}
      >
        Return to Home
      </motion.button>
    </motion.div>
  </motion.div>
)}
  </div>
);
};

export default FormGenerator;